import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import {
  NOTE_MAX,
} from '../../../../../Consts/billing-documents.const';
import {
  CREDIT_NOTE_REASON_LABEL,
  CREDIT_NOTE_REASON_OPTIONS,
} from '../../../../../Enums/money-out.enum';
import {
  CreditNoteDialogData,
  CreditNoteDialogResult,
} from '../../../Interfaces/billing-dialog.interface';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-credit-note-dialog',
  standalone: true,
  imports: [ReactiveFormsModule, MatDialogModule, MatIconModule, TranslateModule],
  templateUrl: './credit-note-dialog.component.html',
  styleUrl: './credit-note-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreditNoteDialogComponent {
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _dialogRef =
    inject<MatDialogRef<CreditNoteDialogComponent, CreditNoteDialogResult>>(MatDialogRef);

  readonly data = inject<CreditNoteDialogData>(MAT_DIALOG_DATA);

  readonly noteMax = NOTE_MAX;
  readonly reasonOptions = CREDIT_NOTE_REASON_OPTIONS;
  readonly reasonLabels = CREDIT_NOTE_REASON_LABEL;

  readonly amount = signal<number>(0);

  readonly isAmountValid = computed<boolean>(
    () => this.amount() > 0 && this.amount() <= this.data.creditableRemainder,
  );

  readonly formGroup: FormGroup = this._formBuilder.group({
    amount: [null, [Validators.required]],
    reason: [this.reasonOptions[0], [Validators.required]],
    refund: [false],
    note: ['', [Validators.maxLength(NOTE_MAX)]],
  });

  constructor() {
    this._registerAmountSync();
  }

  confirm(): void {
    if (!this.formGroup.valid || !this.isAmountValid()) {
      return;
    }

    this._dialogRef.close({
      amount: this.amount(),
      reason: String(this.formGroup.value.reason),
      refund: Boolean(this.formGroup.value.refund) && this.data.isPaid,
      note: String(this.formGroup.value.note ?? '').trim() || null,
    });
  }

  dismiss(): void {
    this._dialogRef.close();
  }

  private _registerAmountSync(): void {
    this.formGroup.controls['amount'].valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((value) => this._applyAmount(Number(value)));
  }

  private _applyAmount(value: number): void {
    this.amount.set(Number.isFinite(value) ? value : 0);
  }
}
